import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { Boxes, ChevronDown, ChevronRight, Plus, Trash2, Download, Upload, Paperclip } from "lucide-react";

const money = (n) => "$" + Number(n || 0).toLocaleString();

function blobDownload(url, filename) {
  return api.get(url, { responseType: "blob" }).then((res) => {
    const u = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a"); a.href = u; a.download = filename; a.click(); window.URL.revokeObjectURL(u);
  });
}

function Section({ cat, users, onChanged }) {
  const [open, setOpen] = useState(true);
  const [data, setData] = useState({ owner: "", custom_fields: [], rows: [] });
  const [overwrite, setOverwrite] = useState(false);
  const cols = [...cat.fields.filter((f) => f.type !== "file"), ...data.custom_fields];
  const load = useCallback(() => api.get(`/inventory/${cat.key}`).then((r) => { setData(r.data); onChanged?.(); }).catch(() => {}), [cat.key, onChanged]);
  useEffect(() => { load(); }, [load]);

  const addRow = async () => { try { await api.post(`/inventory/${cat.key}`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const delRow = async (id) => { if (!window.confirm("Delete this row?")) return; try { await api.delete(`/inventory/${cat.key}/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const patch = async (id, key, val) => { try { await api.patch(`/inventory/${cat.key}/${id}`, { [key]: val }); onChanged?.(); } catch (e) { toast.error(apiErr(e)); } };
  const saveOwner = async (v) => { try { await api.put(`/inventory/${cat.key}/owner`, { owner: v }); } catch (e) { toast.error(apiErr(e)); } };
  const addField = async () => {
    const name = window.prompt("New column name:"); if (!name) return;
    try { await api.post(`/inventory/${cat.key}/field`, { name }); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const clearDemo = async () => { if (!window.confirm("Remove all demo/sample rows in this category?")) return; try { const r = await api.delete(`/inventory/${cat.key}/demo`); toast.success(`Removed ${r.data.deleted} demo rows`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const doImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("overwrite", overwrite ? "true" : "false");
    try { const r = await api.post(`/inventory/${cat.key}/import`, fd); toast.success(`Imported ${r.data.imported} rows${r.data.new_fields.length ? ", added " + r.data.new_fields.length + " new field(s)" : ""}`); load(); }
    catch (er) { toast.error(apiErr(er)); } finally { e.target.value = ""; }
  };
  const uploadAttach = async (id, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/inventory/contract/${id}/attachment`, fd); toast.success("File uploaded"); load(); }
    catch (er) { toast.error(apiErr(er)); } finally { e.target.value = ""; }
  };

  return (
    <section id={`sec-${cat.key}`} className="bg-white rounded-[18px] border border-[#DCE2EA] scroll-mt-24" data-testid={`section-${cat.key}`}>
      <div className="flex flex-wrap items-center gap-3 p-4 border-b border-[#DCE2EA]">
        <button data-testid={`toggle-${cat.key}`} onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 font-bold text-[#002A3A]">
          {open ? <ChevronDown className="h-5 w-5 text-[#BF1722]" /> : <ChevronRight className="h-5 w-5 text-[#BF1722]" />}
          {cat.label} <span className="text-xs font-medium text-[#5A687C]">({data.rows.length})</span>
        </button>
        <div className="ml-auto flex items-center gap-2">
          <label className="text-xs text-[#5A687C]">Owner</label>
          <input data-testid={`owner-${cat.key}`} defaultValue={data.owner} list="inv-users" onBlur={(e) => saveOwner(e.target.value)}
            placeholder="Assign Exec Director…" className="px-3 py-1.5 rounded-lg border border-[#DCE2EA] text-sm w-52" />
        </div>
      </div>
      {open && (
        <div className="p-4">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <button data-testid={`add-row-${cat.key}`} onClick={addRow} className="inline-flex items-center gap-1 bg-[#BF1722] hover:opacity-90 text-white rounded-full px-3.5 py-1.5 text-sm font-semibold"><Plus className="h-4 w-4" /> Add Row</button>
            <button data-testid={`add-field-${cat.key}`} onClick={addField} className="inline-flex items-center gap-1 border border-[#DCE2EA] rounded-full px-3.5 py-1.5 text-sm font-semibold text-[#002A3A]"><Plus className="h-4 w-4" /> Field</button>
            <button data-testid={`template-${cat.key}`} onClick={() => blobDownload(`/inventory/${cat.key}/template`, `template_${cat.key}.csv`)} className="inline-flex items-center gap-1 border border-[#DCE2EA] rounded-full px-3.5 py-1.5 text-sm text-[#002A3A]"><Download className="h-4 w-4" /> Template</button>
            <button data-testid={`export-${cat.key}`} onClick={() => blobDownload(`/inventory/${cat.key}/export`, `inventory_${cat.key}.csv`)} className="inline-flex items-center gap-1 border border-[#DCE2EA] rounded-full px-3.5 py-1.5 text-sm text-[#002A3A]"><Download className="h-4 w-4" /> Export</button>
            <label className="inline-flex items-center gap-1 border border-[#DCE2EA] rounded-full px-3.5 py-1.5 text-sm text-[#002A3A] cursor-pointer"><Upload className="h-4 w-4" /> Import<input data-testid={`import-${cat.key}`} type="file" accept=".csv" onChange={doImport} className="hidden" /></label>
            <label className="inline-flex items-center gap-1.5 text-xs text-[#5A687C]"><input data-testid={`overwrite-${cat.key}`} type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} /> Overwrite on import</label>
            <button data-testid={`clear-demo-${cat.key}`} onClick={clearDemo} className="ml-auto text-xs text-[#BF1722] font-semibold hover:underline">Clear demo data</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-[#002A3A] text-white text-left">
                  {cols.map((c) => <th key={c.key} className="px-2 py-2 font-semibold whitespace-nowrap text-xs">{c.label}</th>)}
                  {cat.key === "contract" && <th className="px-2 py-2 font-semibold text-xs">File</th>}
                  <th className="px-2 py-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && <tr><td colSpan={cols.length + 2} className="px-2 py-6 text-center text-[#98A2AF]">No records. Click "Add Row" or import a CSV.</td></tr>}
                {data.rows.map((row) => (
                  <tr key={row.id} data-testid={`row-${row.id}`} className={`border-b border-[#DCE2EA]/60 ${row.is_demo ? "bg-[#54CBF1]/5" : ""}`}>
                    {cols.map((c) => (
                      <td key={c.key} className="px-1 py-1">
                        {c.type === "select" ? (
                          <select defaultValue={row.data?.[c.key] || ""} onChange={(e) => patch(row.id, c.key, e.target.value)} className="w-full min-w-[120px] px-2 py-1 rounded border border-transparent hover:border-[#DCE2EA] focus:border-[#54CBF1] text-xs bg-transparent">
                            <option value=""></option>
                            {c.options.map((o) => <option key={o} value={o}>{o}</option>)}
                          </select>
                        ) : (
                          <input type={c.type === "date" ? "date" : c.type === "number" ? "number" : "text"} defaultValue={row.data?.[c.key] || ""} onBlur={(e) => patch(row.id, c.key, e.target.value)}
                            className="w-full min-w-[110px] px-2 py-1 rounded border border-transparent hover:border-[#DCE2EA] focus:border-[#54CBF1] text-xs" />
                        )}
                      </td>
                    ))}
                    {cat.key === "contract" && (
                      <td className="px-1 py-1 whitespace-nowrap">
                        {row.data?.attachment_id
                          ? <button onClick={() => blobDownload(`/inventory/contract/${row.id}/attachment`, row.data.attachment || "attachment")} className="text-[#54CBF1] hover:underline text-xs inline-flex items-center gap-1" title={row.data.attachment}><Paperclip className="h-3 w-3" /> {(row.data.attachment || "file").slice(0, 14)}</button>
                          : <label className="text-[#5A687C] text-xs cursor-pointer inline-flex items-center gap-1 hover:text-[#BF1722]"><Upload className="h-3 w-3" /> Upload<input type="file" onChange={(e) => uploadAttach(row.id, e)} className="hidden" /></label>}
                      </td>
                    )}
                    <td className="px-1 py-1 text-center"><button data-testid={`del-${row.id}`} onClick={() => delRow(row.id)} className="text-[#98A2AF] hover:text-[#BF1722]"><Trash2 className="h-4 w-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

const Stat = ({ label, value, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl border border-[#DCE2EA] p-4">
    <div className="text-2xl font-extrabold text-[#002A3A]">{value}</div>
    <div className="text-xs text-[#5A687C] mt-1">{label}</div>
  </div>
);

export default function Inventory() {
  const [cats, setCats] = useState([]);
  const [dash, setDash] = useState(null);
  const [users, setUsers] = useState([]);
  const refreshDash = useCallback(() => api.get("/inventory/dashboard").then((r) => setDash(r.data)).catch(() => {}), []);
  useEffect(() => {
    api.get("/inventory/schema").then((r) => setCats(r.data.categories)).catch(() => {});
    refreshDash();
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [refreshDash]);
  const jump = (k) => document.getElementById(`sec-${k}`)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="space-y-6">
      <datalist id="inv-users">{users.map((u) => <option key={u.id} value={u.name} />)}</datalist>
      <div className="sticky top-0 z-20 -mx-4 lg:-mx-8 px-4 lg:px-8 py-3 bg-[#F4F6FA]/90 backdrop-blur border-b border-[#DCE2EA]">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-xl font-extrabold text-[#002A3A] flex items-center gap-2 mr-2"><Boxes className="h-6 w-6 text-[#BF1722]" /> IT Asset &amp; Contract Inventory</h1>
          {cats.map((c) => <button key={c.key} data-testid={`jump-${c.key}`} onClick={() => jump(c.key)} className="text-xs font-semibold text-[#002A3A] bg-white border border-[#DCE2EA] rounded-full px-3 py-1.5 hover:border-[#BF1722]">{c.label}</button>)}
        </div>
      </div>

      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="inventory-dashboard">
          <Stat testid="stat-pcs" label="PCs / Workstations" value={dash.pcs} />
          <Stat testid="stat-network" label={`Network Devices · R${dash.network_devices.Router} S${dash.network_devices.Switch} F${dash.network_devices.Firewall} AP${dash.network_devices["Access Point"]}`} value={dash.network_total} />
          <Stat testid="stat-printers" label="Printers" value={dash.printers} />
          <Stat testid="stat-cows" label="Computers on Wheels" value={dash.cows} />
          <Stat testid="stat-circuits" label={`Circuits · ${money(dash.circuit_monthly)}/mo`} value={dash.circuits} />
          <Stat testid="stat-contracts" label={`Contracts · ${money(dash.contract_annual)}/yr`} value={dash.contracts} />
          <Stat testid="stat-renewing" label="Contracts renewing ≤180 days" value={dash.contracts_renewing_180} />
          <Stat testid="stat-apps" label={`Applications · ${money(dash.application_annual)}/yr`} value={dash.applications} />
          <div data-testid="stat-expiring" className={`rounded-2xl border p-4 ${dash.expiring_90 > 0 ? "border-[#BF1722] bg-[#BF1722]/5" : "border-[#DCE2EA] bg-white"}`}>
            <div className={`text-2xl font-extrabold ${dash.expiring_90 > 0 ? "text-[#BF1722]" : "text-[#002A3A]"}`}>{dash.expiring_90}</div>
            <div className="text-xs text-[#5A687C] mt-1">Warranty/contract expiring ≤90 days</div>
          </div>
        </div>
      )}

      {cats.map((c) => <Section key={c.key} cat={c} users={users} onChanged={refreshDash} />)}
    </div>
  );
}
